import { Image, Upload, X, Loader2, Crop } from 'lucide-react';
import React, { useState, useCallback, useEffect } from 'react';

import { ImageCropper } from '../../../components/ImageCropper';
import { supabase } from '../../../lib/supabase';
import { useOnboardingStore } from '../../../store/onboarding.store';

interface BrandingStepProps {
  onNext: () => void;
  onBack: () => void;
}

export function BrandingStep({ onNext, onBack }: BrandingStepProps) {
  const { companyData, setCompanyData } = useOnboardingStore();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);

  // Get current user ID for storage path
  useEffect(() => {
    const getUserId = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
      }
    };
    getUserId();
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleFileSelect = (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPG, etc.)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setError(null);
    setOriginalFile(file);
    
    // Create preview URL for cropping
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageToCrop(reader.result as string);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const uploadFile = async (file: File) => {
    if (!userId) {
      setError('Please wait for authentication to complete.');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Generate unique filename
      // Use avatars bucket with user ID as the first folder (required by RLS policy)
      const fileExt = file.name.split('.').pop()?.toLowerCase() || 'png';
      const fileName = `company-logo-${Date.now()}.${fileExt}`;
      // Path format: {user_id}/company-logos/{filename}
      const filePath = `${userId}/company-logos/${fileName}`;

      // Upload to Supabase Storage - avatars bucket
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) {
        setError(`Failed to upload image: ${uploadError.message}`);
        return;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      if (urlData?.publicUrl) {
        setCompanyData({ logoUrl: urlData.publicUrl });
      }
    } catch {
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleCropComplete = async (croppedImageUrl: string) => {
    setShowCropper(false);
    
    // Convert blob URL to File
    const response = await fetch(croppedImageUrl);
    const blob = await response.blob();
    const file = new File([blob], originalFile?.name || 'logo.png', {
      type: blob.type || 'image/png',
    });

    // Upload the cropped image
    await uploadFile(file);
    
    // Clean up
    URL.revokeObjectURL(croppedImageUrl);
    setImageToCrop(null);
    setOriginalFile(null);
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setImageToCrop(null);
    setOriginalFile(null);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const removeLogo = () => {
    setCompanyData({ logoUrl: '' });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNext();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-[#9B1421]/20 flex items-center justify-center mx-auto mb-4">
          <Image size={32} className="text-rollops-accent" />
        </div>
        <h2 className="text-2xl font-bold text-[#EBFFEE]">Branding</h2>
        <p className="text-[#A9A3A3] mt-2">Add your gym&apos;s logo (optional)</p>
      </div>

      {/* Logo Upload Area */}
      <div className="space-y-4">
        {companyData.logoUrl ? (
          // Logo Preview
          <div className="relative w-48 h-48 mx-auto">
            <img
              src={companyData.logoUrl}
              alt="Gym logo"
              className="w-full h-full object-contain rounded-xl bg-[#504A4A] p-4"
            />
            <div className="absolute -top-2 -right-2 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  // Re-open cropper with current logo
                  setImageToCrop(companyData.logoUrl);
                  setShowCropper(true);
                }}
                className="w-8 h-8 bg-[#9B1421] hover:bg-[#7A0F1A] rounded-full flex items-center justify-center text-white transition-colors"
                title="Crop logo"
              >
                <Crop size={14} />
              </button>
              <button
                type="button"
                onClick={removeLogo}
                className="w-8 h-8 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center text-white transition-colors"
                title="Remove logo"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        ) : (
          // Upload Drop Zone
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
              dragActive
                ? 'border-[#9B1421] bg-[#9B1421]/10'
                : 'border-[#504A4A] hover:border-[#9B1421]/50'
            }`}
          >
            <input
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={uploading}
            />
            <div className="flex flex-col items-center gap-3">
              {uploading ? (
                <>
                  <Loader2 size={48} className="text-rollops-accent animate-spin" />
                  <p className="text-[#EBFFEE]">Uploading...</p>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-full bg-[#504A4A] flex items-center justify-center">
                    <Upload size={24} className="text-[#A9A3A3]" />
                  </div>
                  <div>
                    <p className="text-[#EBFFEE] font-medium">
                      Drop your logo here, or{' '}
                      <span className="text-rollops-accent">browse</span>
                    </p>
                    <p className="text-sm text-[#A9A3A3] mt-1">
                      PNG, JPG up to 5MB
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {error && (
          <p className="text-red-500 text-sm text-center">{error}</p>
        )}

        <p className="text-xs text-[#A9A3A3] text-center">
          Your logo will be displayed on invoices, emails, and in the app.
          You can always add or change this later.
        </p>
      </div>

      {/* Navigation Buttons */}
      <div className="flex gap-4">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] text-white font-semibold rounded-lg transition-colors"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={uploading}
          className="flex-1 py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
        >
          {companyData.logoUrl ? 'Continue' : 'Skip for Now'}
        </button>
      </div>

      {/* Image Cropper Modal */}
      {showCropper && imageToCrop && (
        <ImageCropper
          image={imageToCrop}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          aspectRatio={1}
          cropShape="rect"
          minZoom={1}
          maxZoom={3}
        />
      )}
    </form>
  );
}

